import Vue from 'vue';
import _ from 'lodash';
import axios from 'axios';
import StackTrace from 'stacktrace-js';
import browserService from 'pdk/services/browser';
import { Service } from 'pdk/services';
import AppConfig from '@/config';


export class LoggerService extends Service {
    constructor() {
        super();

		this._sessionDurationKey = 'session-age';
		this._init();
	}

    error(error, logger) {
        return this._error(error, 'Error', logger || 'Unknown');
	}

    fatal(error, logger) {
        return this._error(error, 'Fatal', logger || 'Unknown');
	}
	
	_init() {
		window.sessionStorage.setItem(this._sessionDurationKey, this._getCurrentTimestamp());
	}

	_getCurrentTimestamp() {
		return Math.round((new Date()).getTime() / 1000);
	}

	_getSessionStartTime() {
		return +window.sessionStorage.getItem(this._sessionDurationKey);
	}

	_getSessionDuration() {
		const start = this._getSessionStartTime();
		if(start == null || isNaN(start)) return -1;

		const result = this._getCurrentTimestamp() - start;
		if(result < 0) return -1;

		return result;
	}

    _error(error, level, logger) {
        // Don't send logs if we're not in production
        if (!AppConfig.env.production) return;

		if (error instanceof Error){
			this._buildErrorContext(error)
				.then((ex) => {
					const request = this._buildErrorRequest(ex, level, logger);
					this._send(request);
			});
		}
		else {
			this._send(error);
		}	
	}

	_buildErrorContext(error) {
		return new Promise((resolve, reject) => {

			const type = error.name;
			const message = `${type}: ${error.message}`;

            if (!AppConfig.env.production) {
				resolve({
					error,
					type,
					message
				});
				return;
			}

			StackTrace.fromError(error)
				.then((frames) => {
					frames = frames || [];

					// Build some simple variables that we can reuse
					const action = frames.length ? frames[0].functionName : null;

					// Build the stack
					const serializedFrames = _.map(frames, (f) => `at ${f.functionName} (${f.fileName}:${f.lineNumber})`);
					const stack = serializedFrames.join('\n');

					// Build the exception
					const exception = `${message }\n${ _.map(serializedFrames, (f) => `    ${f}`).join('\n')}`;

					// Return the context
					resolve({
						error,
						type,
						message,
						action,
						frames,
						stack,
						exception
					});
				})
				.catch(() => {
					// TODO: What happens when the context builder fails?
					reject();
			});
		});
	}

    _buildErrorRequest(ex, level, logger) {
		return {
			level,
			logger,
			message: ex.message,
			action: ex.action,
			callsite: ex.stack,
			exception: ex.exception
		};
	}

	_send(options) {
		const browser = browserService.getBrowser();
		const platform = browserService.getPlatform();

		const request = this._extend(options, {
			siteName: `${AppConfig.id} (${AppConfig.env.mode})`,
			version: AppConfig.version,
			loggedOn: new Date(),
			level: null,
			username: Vue.$auth.tokenParsed.GlobalUserId,
			logger: null,
			message: null,
			serverName: `PortalID: ${AppConfig.context.portalID}`,
			url: window.location.href,
			https: window.location.protocol === 'https:' ? 1 : 0,
			action: null,
			callsite: null,
			exception: null,
			platform: `${platform.name} ${platform.version}`,
			browser: browser.name,
			browserVersion: browser.version,
			viewport: `${browserService.getViewportWidth()}x${browserService.getViewportHeight()}`,
			mobile: browser.mobile ? 1 : 0,
			sessionSeconds: this._getSessionDuration()
		});

        const headers = {
            'portalid': AppConfig.context.portalID
        };
        const accessToken = Vue.$auth.token;
        if (accessToken) {
            headers['authorization'] = `Bearer ${accessToken}`;
        }

        // Send the request
        axios({
            method: 'post',
            url: `${AppConfig.api.baseURL}/api/general/logerror`,
            data: request,
            headers
        })
            .catch(function (error) {
                console.error('Log error', error);
            });
	}
};

export default new LoggerService();
